import React, { useState, useEffect } from 'react'
import Button from '../../elementals/button'
import Modal, { ModalProps } from '../modal'
import VerticalExpander from '../../elementals/verticalExpander'
import Animate from '../../elementals/animate'

const DURATION_MS = 300

/**
 * This can be overridden by passing a custom footer component.
 */
export type DefaultFooterProps = {
  onClose: () => void
  isNextStepEnabled?: (currentStep: number) => boolean
  setStep: (step: number) => void
  step: number
  maxSteps: number
}

export const DefaultFooter = ({
  onClose,
  isNextStepEnabled = () => true,
  setStep,
  step,
  maxSteps,
}: DefaultFooterProps) => (
  <div
    style={{
      width: '100%',
      display: 'inline-flex',
      justifyContent: 'flex-end',
      gap: '12px',
    }}
  >
    <Button
      disabled={step < 1}
      role='secondary'
      elmId='back_button'
      onClick={() => {
        setStep(step - 1)
      }}
    >
      Back
    </Button>
    {step === maxSteps - 1 ? (
      <Button
        disabled={!isNextStepEnabled(step)}
        className='done-button'
        role='primary'
        elmId='done_button'
        onClick={onClose}
      >
        Done
      </Button>
    ) : (
      <Button
        disabled={!isNextStepEnabled(step)}
        role='primary'
        elmId='next_button'
        onClick={() => {
          setStep(step + 1)
        }}
      >
        Next
      </Button>
    )}
  </div>
)

type MultiStepModalProps = {
  trigger: React.ReactNode
  onClose: () => void
  isOpen: boolean
  title: ModalProps['title']
  fullScreen: ModalProps['fullScreen']
  size: ModalProps['size']
  steps: { title: string; content: React.ReactNode }[]
  isNextStepEnabled?: (currentStep: number) => boolean
  customFooter?: React.ComponentType<DefaultFooterProps>
}

const MultiStepModal = ({
  trigger,
  onClose,
  isOpen,
  title,
  fullScreen,
  size,
  steps,
  isNextStepEnabled = () => true,
  customFooter,
}: MultiStepModalProps) => {
  const [step, setStep] = useState(0)
  const [maxSteps, setMaxSteps] = useState(steps.length)
  const [stepDelayed, setStepDelayed] = useState(0)
  const [isVisible, setIsVisible] = useState(true)

  const customFooterElement = customFooter
    ? React.createElement(customFooter, {
        onClose,
        setStep,
        step,
        maxSteps,
        isNextStepEnabled,
      })
    : null

  useEffect(() => {
    setMaxSteps(steps.length)
  }, [steps])

  useEffect(() => {
    if (isOpen) {
      setStep(0)
    }
  }, [isOpen])

  /**
   * stepDelayed is used to hold the current step while the fade-in/out animation is happening.
   */
  useEffect(() => {
    setIsVisible(false)
    setTimeout(() => {
      setStepDelayed(step)
      setIsVisible(true)
    }, DURATION_MS)
  }, [step])

  return (
    <Modal
      title={steps[step].title || title}
      trigger={trigger}
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      size={size}
    >
      <Modal.Content>
        <VerticalExpander>
          <Animate.FadeInOut isVisible={isVisible} duration={DURATION_MS}>
            {steps[stepDelayed].content}
          </Animate.FadeInOut>
        </VerticalExpander>
      </Modal.Content>

      <Modal.Actions>
        {customFooterElement || (
          <DefaultFooter
            onClose={onClose}
            setStep={setStep}
            step={step}
            maxSteps={maxSteps}
            isNextStepEnabled={isNextStepEnabled}
          />
        )}
      </Modal.Actions>
    </Modal>
  )
}

export default MultiStepModal
